import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract: forbidden. The picture is the cover itself, read
// through a conformal map; a light on top of it would only wash the cover.
const RIG = 'forbidden';
const QUAD_MARGIN_Y = 1.3;   // the quad's height over the frame's: room for the camera drift
export { RIG };

export const PROFILE = {
  id: 'loxodrome',
  family: 'shader',
  danceability: 0.80,
  energyLevel: 0.76,
  bpmMin: 105,
  bpmMax: 170,
  movement: 0.80,
  tonality: 0.65,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Loxodrome: the cover, tiled to infinity, caught in an animated Moebius flow.
//
// A MOEBIUS TRANSFORMATION is the conformal deformation of the complex plane:
// circles go to circles, angles are kept. It has two fixed points, and by
// the multiplier's type the plane turns around them (elliptic), pours from
// one to the other (hyperbolic) or SPIRALS from one to the other
// (loxodromic). For a pixel z (screen centre 0, height 2), fixed points p, q
// and multiplier k:
//     w  = (z - p) / (z - q)        p to 0, q to infinity
//     w  = w * k                    turn by arg(k), scale by |k|
//     z' = (p - q * w) / (1 - w)    and back
// and the cover is read at z'. Everything is exact: no seam, no feedback.
//
// THE PARAMETERS ARE ACCUMULATED, NOT FUNCTIONS OF TIME. k = exp(a + i theta)
// is an exponential, so adding to a and theta COMPOSES transformations, and
// composition is what keeps the flow continuous: a beat adds a sixteenth of a
// turn to theta (interpolated over 120 ms), the flow rate adds to a every
// frame, a bar adds an impulse, a drop adds an implosion. Nothing is ever
// recomputed from scratch.
//
// WHY |a| IS BOUNDED. The map's derivative at the source is k: the WHOLE
// plane is magnified by |k|, not just the neighbourhood of the sink. Left
// to accumulate, a of 5 shrinks every tile to a few pixels and the frame is
// a uniform mipmap grey - the sink has eaten the picture. The flow therefore
// runs at a rate that reaches the sink's neighbourhood from halfway in eight
// beats, the phrase inversions bring a back, and past A_MAX the direction is
// turned around early rather than letting the frame go grey.
//
// THE TILING is a mirror repetition on both axes (abs(fract), Tessel's
// fold): no seam anywhere, and the mirror symmetry is already a kaleidoscope
// the Moebius then winds up. The mip level is computed from the UNFOLDED
// coordinate: the fold's own derivative spike would otherwise draw a grey
// hairline along every tile edge.

// --- the flow -------------------------------------------------------------------
const THETA_STEP = Math.PI * 2 / 16;   // per beat
const STEP_S = 0.12;                   // the step's interpolation
const SINK_REACH = 3.0;                // |w| that counts as the sink's neighbourhood
const SINK_BEATS = 8;                  // from halfway (|w| = 1) in this many beats
// Measured on the harness cover: at a = 2 four fifths of the frame is one
// flat colour of the cover, at 3.4 all of it. 1.8 keeps the picture.
const A_MAX = 1.8;                     // beyond this the frame is one colour: turn around
const BAR_IMPULSE = 0.15;              // extra a on a bar, over
const BAR_IMPULSE_S = 0.30;
const LOW_SWING = 0.30;                // bands.low: +-30% on the flow rate
const MID_SWING = 0.15;                // bands.mid: +-15% on the tiling scale
const JUMP_BEATS = 2;                  // fixed points and scale interpolate over 2 beats
const DRIFT_R = 0.30;
const DRIFT_PERIODS = [23, 37];
const DIST_MIN = 0.6, DIST_MAX = 1.4;
const SCALES = [0.4, 0.55, 0.8, 1.1, 1.6];
const SCALE_DEFAULT = 0.8;
const DROP_LEAD_BEATS = 4;
const DROP_IMPLODE = 3.2;              // a added over one beat: from |a| <= 1.8 that is e^5, the whole plane in the sink
const DROP_REBIRTH_BARS = 2;           // then back out, violently, over two bars
const DOUBLE_RATE = 0.6;               // the second map runs slower and turns the other way
const BEATS_PER_BAR = 4;
const BEATS_PER_PHRASE = 16;
const MAJOR_EVERY = 4;                 // phrases per major phrase
const SYNTH_BPM = 128;

// --- the picture ------------------------------------------------------------------
const VIGNETTE = 0.85;
const LINES_ALPHA = 0.08;
const ABERRATION_R = 0.15;
const ABERRATION_PX = 2.0;
const GREY_FROM = 6.0, GREY_TO = 9.0;  // mip levels: toward mid grey beyond 6
const SAFE_FLOOR = 0.5;

const MODES = ['loxo', 'elliptic', 'hyper', 'double'];
const TIER = {
  0: { modes: ['loxo', 'elliptic'], aberration: false, lines: false },
  1: { modes: ['loxo', 'elliptic', 'hyper'], aberration: true, lines: false },
  2: { modes: ['loxo', 'elliptic', 'hyper', 'double'], aberration: true, lines: true }
};
const DEFAULT_TIER = 2;
const FALLBACK_VIVID = [0x36e0ff, 0xff5bd0, 0xffb02e, 0x7cff5a];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(t) { t = clamp01(t); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function rand(a, b) { return a + Math.random() * (b - a); }
function hexToRgb(hex) { return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255]; }

// The map on the CPU, the same arithmetic as the shader: for the tests and
// for anything that needs to know where a screen point reads from.
export function mobius(z, p, q, a, theta) {
  const nx = z[0] - p[0], ny = z[1] - p[1];
  const dx = z[0] - q[0], dy = z[1] - q[1];
  const dd = dx * dx + dy * dy || 1e-12;
  let wx = (nx * dx + ny * dy) / dd, wy = (ny * dx - nx * dy) / dd;
  const m = Math.exp(a), kc = m * Math.cos(theta), ks = m * Math.sin(theta);
  const tx = wx * kc - wy * ks, ty = wx * ks + wy * kc;
  wx = tx; wy = ty;
  const ax = p[0] - (q[0] * wx - q[1] * wy), ay = p[1] - (q[0] * wy + q[1] * wx);
  const bx = 1 - wx, by = -wy;
  const bb = bx * bx + by * by || 1e-12;
  return [(ax * bx + ay * by) / bb, (ay * bx - ax * by) / bb];
}

// =============================================================================
// SHADERS
// =============================================================================

const VERTEX = `
  precision highp float;
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

// PREC is the Moebius arithmetic's precision: highp, or mediump under
// ?loxoprec=mediump to see the artefacts near the fixed points for oneself.
function fragment(prec) {
  return `
  precision ${prec} float;
  uniform sampler2D uTex;
  uniform float uHasTex;
  uniform vec2 uTexSize;
  // uAspect comes with the safe-zone chunk (the same shared uniform)
  uniform vec2 uP;
  uniform vec2 uQ;
  uniform vec2 uK;            // a, theta
  uniform vec2 uP2;
  uniform vec2 uQ2;
  uniform vec2 uK2;
  uniform float uDouble;
  uniform float uScale;       // one cover, in plane units
  uniform vec3 uTint;
  uniform float uAberration;  // 0..1
  uniform float uLines;
  uniform float uFixed;
  uniform float uTime;
  uniform float uPx;          // one pixel, in plane units
  uniform vec3 uColA;
  uniform vec3 uColB;
  uniform float uLodBias;
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}
  float safeAt(vec4 clip) {
    float a = safeZoneAttenuation(clip);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((a - 0.32) / 0.68, 0.0, 1.0));
  }
  vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
  vec2 cdiv(vec2 a, vec2 b) { float d = max(dot(b, b), 1e-12); return vec2(a.x * b.x + a.y * b.y, a.y * b.x - a.x * b.y) / d; }
  vec2 mobius(vec2 z, vec2 p, vec2 q, vec2 k) {
    vec2 w = cdiv(z - p, z - q);
    vec2 kc = exp(k.x) * vec2(cos(k.y), sin(k.y));
    w = cmul(w, kc);
    return cdiv(p - cmul(q, w), vec2(1.0, 0.0) - w);
  }
  vec2 flowTo(vec2 z) {
    vec2 zp = mobius(z, uP, uQ, uK);
    if (uDouble > 0.5) zp = mobius(zp, uP2, uQ2, uK2);
    return zp;
  }
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  // the cover, tiled in mirror, read at the unfolded coordinate u (period 1)
  vec3 tileAt(vec2 u, float lod) {
    if (uHasTex > 0.5) {
      // the texture wraps in MIRRORED repeat: the fold is the sampler's and
      // the coordinate stays continuous; the mip level is the one computed
      // from that coordinate's derivatives (the hardware's own choice came out
      // several levels too blurry under ANGLE/Metal)
      vec3 c = texture2DLodEXT(uTex, u, lod).rgb;
      return mix(c, vec3(0.42), smoothstep(${GREY_FROM.toFixed(1)}, ${GREY_TO.toFixed(1)}, lod));
    }
    vec2 m = abs(fract(u * 0.5) * 2.0 - 1.0);
    // no cover: a two-colour checker with rings, high contrast
    vec2 g = fract(m * 4.0);
    float ck = step(0.5, g.x) != step(0.5, g.y) ? 1.0 : 0.0;
    float ring = 1.0 - smoothstep(0.0, 0.06, abs(length(g - 0.5) - 0.32));
    vec3 c = mix(uColA, uColB, ck);
    c = mix(c, vec3(1.0), ring * 0.8);
    return mix(c, vec3(0.42), smoothstep(${GREY_FROM.toFixed(1)}, ${GREY_TO.toFixed(1)}, lod));
  }
  vec3 readAt(vec2 z) {
    vec2 zp = flowTo(z);
    vec2 u = zp / uScale;                  // in covers: one cover = uScale plane units
    // mip level from the UNFOLDED coordinate: the fold's derivative spike
    // would draw a grey hairline along every tile edge
    vec2 dx = dFdx(u) * uTexSize, dy = dFdy(u) * uTexSize;
    float lod = 0.5 * log2(max(dot(dx, dx), dot(dy, dy)) + 1e-12);
    return tileAt(u, max(0.0, lod + uLodBias));
  }
  void main() {
    vec2 z = (vUv - 0.5) * 2.0 * vec2(uAspect, 1.0);
    vec3 col = readAt(z);

    // chromatic aberration near the fixed points: the light disperses where
    // the flow is most violent
    if (uAberration > 0.001) {
      vec2 dp = z - uP, dq = z - uQ;
      float lp = length(dp), lq = length(dq);
      float sp = 1.0 - smoothstep(0.0, ${ABERRATION_R.toFixed(2)}, lp);
      float sq = 1.0 - smoothstep(0.0, ${ABERRATION_R.toFixed(2)}, lq);
      float s = max(sp, sq) * uAberration;
      if (s > 0.01) {
        vec2 dir = normalize(sp > sq ? dp : dq + vec2(1e-6));
        vec2 off = dir * ${ABERRATION_PX.toFixed(1)} * uPx * s;
        col.r = readAt(z + off).r;
        col.b = readAt(z - off).b;
      }
    }

    // the orbits of the flow: level lines of |w| and arg(w), thin and light
    if (uLines > 0.5) {
      vec2 w = cdiv(z - uP, z - uQ);
      float lr = log(max(length(w), 1e-6)) / 0.405;         // rings at |w| ratios of 1.5
      float la = atan(w.y, w.x) / 0.3927;                    // spokes every 22.5 degrees
      float fr = abs(fract(lr) - 0.5), fa = abs(fract(la) - 0.5);
      float wr = fwidth(lr), wa = fwidth(la);
      float line = max(1.0 - smoothstep(0.5 - wr * 1.5, 0.5, fr), 1.0 - smoothstep(0.5 - wa * 1.5, 0.5, fa));
      line *= 1.0 - smoothstep(0.0, 0.05, min(length(z - uP), length(z - uQ)));   // not on the points themselves
      line *= step(wr, 0.5) * step(wa, 0.5);                                       // gone where they would alias
      col = mix(col, uTint, line * ${LINES_ALPHA.toFixed(2)});
    }

    // vignette toward the edges, tinted: the edges are where the tiling is densest
    float edge = smoothstep(0.55, 1.35, length(z));
    col *= mix(1.0, ${VIGNETTE.toFixed(2)}, edge);
    col = mix(col, col * uTint, edge * 0.25);

    // dither
    col += (hash21(gl_FragCoord.xy * 0.001 + fract(uTime * 0.1)) - 0.5) / 255.0;

    if (uFixed > 0.5) {
      vec2 ap = abs(z - uP), aq = abs(z - uQ);
      float cp = step(min(ap.x, ap.y), uPx * 1.2) * step(max(ap.x, ap.y), 0.05);
      float cq = step(min(aq.x, aq.y), uPx * 1.2) * step(max(aq.x, aq.y), 0.05);
      col = mix(col, vec3(1.0, 0.15, 0.1), cp);
      col = mix(col, vec3(0.1, 0.6, 1.0), cq);
    }
    gl_FragColor = vec4(col * safeAt(vClip), 1.0);
  }
`;
}

// =============================================================================
// THE VISUALIZER
// =============================================================================

export class LoxodromeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._beatClock = 0;
    this._lastBeat = null;
    this._lastBarIndex = null;
    this._lastPhraseIndex = null;
    this._synthBeat = 0;
    this._beatCount = 0;
    this._phraseCount = 0;
    this._bpm = SYNTH_BPM;

    // the accumulated parameters
    this._a = 0;
    this._theta = 0;
    this._thetaFrom = 0; this._thetaTo = 0; this._stepT = 1;
    this._dir = 1;                 // the flow's direction: +1 source -> sink
    this._impulseT = 1e9;
    this._a2 = 0; this._theta2 = 0;
    this._dropT = -1;              // seconds since the drop, or -1
    this._dropLead = 0;            // 0..1, the four beats before a drop
    this._lastDropTime = -1e9;

    // the fixed points: anchors, drift, and the jump in progress
    this._anchor = { p: [-0.5, 0.1], q: [0.5, -0.1] };
    this._prevAnchor = { p: [-0.5, 0.1], q: [0.5, -0.1] };
    this._jumpT = 1e9;
    this._p = [0, 0]; this._q = [0, 0];
    this._anchor2 = { p: [0.2, 0.55], q: [-0.3, -0.5] };
    this._scaleFrom = SCALE_DEFAULT; this._scaleTo = SCALE_DEFAULT; this._scaleT = 1e9;
    this._scale = SCALE_DEFAULT;
    this._lowBand = 0.5; this._midBand = 0.5; this._highBand = 0.5; this._live = false;
    this._modeIndex = 0;
    this._mode = 'loxo';
    this._lastPalette = null;
    this._lastArtworkUrl = null;
    this._tint = [1, 1, 1];

    const m = urlParam('loxomode');
    this._forcedMode = m && MODES.indexOf(m) >= 0 ? m : null;
    if (this._forcedMode) this._mode = this._forcedMode;
    this._fixedDebug = urlParam('loxofixed') === '1';
    this._noTex = urlParam('loxonotex') === '1';
    this._dropDebug = urlParam('loxodrop') === '1';
    // ?loxoanchor=px,py,qx,qy: fixed anchors, no drift, no jump - for comparing two captures
    const an = urlParam('loxoanchor');
    this._forcedAnchor = null;
    if (an) { const v = an.split(',').map(parseFloat); if (v.length === 4 && v.every((x) => isFinite(x))) this._forcedAnchor = { p: [v[0], v[1]], q: [v[2], v[3]] }; }
    const lodBias = urlParam('loxolod');
    this._lodBias = lodBias != null && lodBias !== '' ? parseFloat(lodBias) : 0;
    const k = urlParam('loxok');
    this._forcedK = null;
    if (k) { const parts = k.split(',').map(parseFloat); if (parts.length === 2 && parts.every((x) => isFinite(x))) this._forcedK = parts; }
    this._stepDebug = urlParam('loxostep') === '1';
    this._stepDone = false;
    const prec = urlParam('loxoprec');
    this._precision = prec === 'mediump' ? 'mediump' : 'highp';
    this._postProcessing = { bloomStrength: 0.25, vignette: 0.0, aberration: 0.0, grain: 0.015 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, 10);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    const HH = 10 * Math.tan(camera.fov * Math.PI / 360);
    this._HH = HH;

    this._uniforms = Object.assign({
      uTex: { value: null }, uHasTex: { value: 0 }, uTexSize: { value: new THREE.Vector2(1024, 1024) },
      uP: { value: new THREE.Vector2() }, uQ: { value: new THREE.Vector2() }, uK: { value: new THREE.Vector2() },
      uP2: { value: new THREE.Vector2() }, uQ2: { value: new THREE.Vector2() }, uK2: { value: new THREE.Vector2() },
      uDouble: { value: 0 }, uScale: { value: SCALE_DEFAULT },
      uTint: { value: new THREE.Vector3(1, 1, 1) }, uAberration: { value: 0 },
      uLines: { value: this._layout.lines ? 1 : 0 }, uFixed: { value: this._fixedDebug ? 1 : 0 },
      uTime: { value: 0 }, uPx: { value: 2 / 720 }, uLodBias: { value: this._lodBias },
      uColA: { value: new THREE.Vector3(...hexToRgb(FALLBACK_VIVID[0])) }, uColB: { value: new THREE.Vector3(...hexToRgb(FALLBACK_VIVID[1])) }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms, vertexShader: VERTEX, fragmentShader: fragment(this._precision),
      depthTest: false, depthWrite: false,
      extensions: { derivatives: true, shaderTextureLOD: true }
    });
    // a quad that covers the frame at the camera's distance, whatever the aspect,
    // WITH A MARGIN in height: scene-manager's drift moves the camera (+-0.13
    // up, +-0.012 rad, a dolly) and a quad exactly the frame's height showed
    // its top edge as a thin black triangle. uv stays tied to the frame (see
    // _fitUv), so the margin changes nothing inside it.
    this._geo = new THREE.PlaneGeometry(2 * HH * 4, 2 * HH * QUAD_MARGIN_Y);
    this._mesh = new THREE.Mesh(this._geo, this._material);
    this._mesh.frustumCulled = false;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._uniforms.uPx.value = 2 / Math.max(1, _size.y);
    };
    scene.add(this._mesh);
    // the quad is wider than the frame: uv must map the FRAME, so the geometry's
    // own uv is replaced by one spanning the visible width
    this._fitUv();
    if (this._forcedAnchor) { this._anchor = { p: this._forcedAnchor.p.slice(), q: this._forcedAnchor.q.slice() }; this._prevAnchor = { p: this._forcedAnchor.p.slice(), q: this._forcedAnchor.q.slice() }; }
    this._placePoints(0);
  }

  _fitUv() {
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const uv = this._geo.attributes.uv;
    // the plane is 8:1 in world; the frame is `aspect`:1 - uv x spans the frame
    // uv.x = 0.5 + worldX / (2 * HH * aspect): the frame's width maps 0..1
    for (let i = 0; i < uv.count; i++) {
      const wx = this._geo.attributes.position.getX(i);
      uv.setX(i, 0.5 + wx / (2 * this._HH * aspect));
      uv.setY(i, 0.5 + this._geo.attributes.position.getY(i) / (2 * this._HH));
    }
    uv.needsUpdate = true;
    this._aspect = aspect;
  }

  // --- the fixed points --------------------------------------------------------

  _inSafeZone(x, y, margin) {
    // R19: the zone is the stage block, not the cover; no cover, no zone (as before)
    if (safeZoneUniforms.uSafeHalfSize.value.x <= 0) return false;
    const c = safeZoneUniforms.uStageCenter.value, h = safeZoneUniforms.uStageHalfSize.value;
    const f = safeZoneUniforms.uSafeFeather.value + (margin || 0);
    return Math.abs(x - c.x) < h.x + f && Math.abs(y - c.y) < h.y + f;
  }
  _pickAnchors() {
    const aspect = this._aspect || 1.78;
    const xr = aspect * 0.92, yr = 0.85;
    for (let t = 0; t < 400; t++) {
      const p = [rand(-xr, xr), rand(-yr, yr)];
      const q = [rand(-xr, xr), rand(-yr, yr)];
      const d = Math.hypot(p[0] - q[0], p[1] - q[1]);
      if (d < DIST_MIN || d > DIST_MAX) continue;
      // the drift circle must stay out of the cover's zone too, and so must
      // the path from the previous anchors (the jump interpolates along it)
      if (this._inSafeZone(p[0], p[1], DRIFT_R) || this._inSafeZone(q[0], q[1], DRIFT_R)) continue;
      if (this._anchor && (this._crossesZone(this._anchor.p, p) || this._crossesZone(this._anchor.q, q))) continue;
      return { p, q };
    }
    // a zone so large nothing fits (the fixed constants before any overlay
    // measurement): the frame's sides are always outside it
    const side = this._anchor && this._anchor.p[0] < 0 ? -1 : 1;
    return { p: [side * aspect * 0.86, rand(0.3, 0.6)], q: [side * aspect * 0.86 - side * 0.35, rand(-0.6, -0.3)] };
  }
  _crossesZone(a, b) {
    for (let k = 0; k <= 10; k++) { const t = k / 10; if (this._inSafeZone(lerp(a[0], b[0], t), lerp(a[1], b[1], t), DRIFT_R)) return true; }
    return false;
  }
  _jumpPoints() {
    if (this._forcedAnchor) return;
    this._prevAnchor = { p: this._anchor.p.slice(), q: this._anchor.q.slice() };
    this._anchor = this._pickAnchors();
    this._anchor2 = this._pickAnchors();
    this._jumpT = 0;
  }
  _placePoints(dt) {
    this._jumpT += dt;
    const beatS = 60 / this._bpm;
    const k = smooth(this._jumpT / (JUMP_BEATS * beatS));
    const t = this._forcedAnchor ? 0 : this._time;
    const dr = this._forcedAnchor ? 0 : DRIFT_R;
    const dp = [Math.cos(t * Math.PI * 2 / DRIFT_PERIODS[0]) * dr, Math.sin(t * Math.PI * 2 / DRIFT_PERIODS[0]) * dr];
    const dq = [Math.cos(t * Math.PI * 2 / DRIFT_PERIODS[1] + 2.1) * dr, Math.sin(t * Math.PI * 2 / DRIFT_PERIODS[1] + 2.1) * dr];
    for (let i = 0; i < 2; i++) {
      this._p[i] = lerp(this._prevAnchor.p[i], this._anchor.p[i], k) + dp[i];
      this._q[i] = lerp(this._prevAnchor.q[i], this._anchor.q[i], k) + dq[i];
    }
    this._uniforms.uP.value.set(this._p[0], this._p[1]);
    this._uniforms.uQ.value.set(this._q[0], this._q[1]);
    this._uniforms.uP2.value.set(this._anchor2.p[0] - dq[0] * 0.5, this._anchor2.p[1] - dq[1] * 0.5);
    this._uniforms.uQ2.value.set(this._anchor2.q[0] + dp[0] * 0.5, this._anchor2.q[1] + dp[1] * 0.5);
  }

  // --- the clock ---------------------------------------------------------------

  _advanceClock(dt, profile) {
    const hasGrid = profile.beatIndex != null && profile.beatPhase != null;
    let beatClock;
    if (hasGrid) {
      beatClock = profile.beatIndex + profile.beatPhase;
      if (this._lastBeat != null && Math.abs(beatClock - this._beatClock) > 4) { this._lastBeat = null; this._lastBarIndex = null; this._lastPhraseIndex = null; }
    } else {
      this._synthBeat += dt * this._bpm / 60;
      beatClock = this._synthBeat;
    }
    this._beatClock = beatClock;
    const beat = Math.floor(beatClock);
    if (this._lastBeat == null) { this._lastBeat = beat; return; }
    while (this._lastBeat < beat) {
      this._lastBeat++;
      this._beatCount++;
      this._onBeat();
      if (hasGrid) {
        const bar = profile.barIndex;
        if (bar != null && bar !== this._lastBarIndex) { if (this._lastBarIndex != null) this._onBar(); this._lastBarIndex = bar; }
        const phrase = profile.phraseIndex;
        if (phrase != null && phrase !== this._lastPhraseIndex) { if (this._lastPhraseIndex != null) this._onPhrase(phrase); this._lastPhraseIndex = phrase; }
      } else {
        if (this._lastBeat % BEATS_PER_BAR === 0) this._onBar();
        if (this._lastBeat % BEATS_PER_PHRASE === 0) this._onPhrase(this._lastBeat / BEATS_PER_PHRASE);
      }
    }
  }

  _onBeat() {
    if (this._stepDebug || this._forcedK) return;
    if (this._mode === 'hyper') return;              // pure flow, no turn
    this._step();
  }
  _step() {
    this._thetaFrom = this._theta;
    this._thetaTo = this._thetaTo + THETA_STEP * this._dir;
    this._stepT = 0;
  }
  _onBar() {
    if (this._mode === 'elliptic' || this._forcedK) return;
    this._impulseT = 0;
  }
  _onPhrase(phraseIndex) {
    this._phraseCount++;
    this._jumpPoints();
    this._dir = -this._dir;                          // source and sink swap roles
    // the tiling's scale
    this._scaleFrom = this._scale;
    this._scaleTo = SCALES[(this._phraseCount * 3 + 1) % SCALES.length];
    this._scaleT = 0;
    // a major phrase changes the regime
    if (!this._forcedMode && phraseIndex % MAJOR_EVERY === 0) {
      const list = this._layout.modes;
      this._modeIndex = (this._modeIndex + 1) % list.length;
      this._mode = list[this._modeIndex];
    }
  }

  // --- the frame -----------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    this._time += dt;
    this._uniforms.uTime.value = this._time;

    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._fitUv();

    const palette = profile.artworkPalette;
    if (palette !== this._lastPalette) { this._lastPalette = palette; this._setPalette(palette); }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl && !this._noTex) this._loadCover(profile.artworkUrl);
      else this._setTexture(null);
    }

    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    const beatS = 60 / this._bpm;
    const director = profile.director || {};
    this._live = !!(director.hasReliableGrid && director.bands);
    if (this._live) {
      this._lowBand = clamp01(director.bands.low); this._midBand = clamp01(director.bands.mid); this._highBand = clamp01(director.bands.high);
    }
    this._advanceClock(dt, profile);

    // the drop: four beats of acceleration, one beat of implosion, two bars of rebirth
    let ev = director.nextEvent;
    // ?loxodrop=1: a drop at 3 s, with its four beats of run-up
    if (this._dropDebug) { const ms = (3.0 - this._time) * 1000; ev = ms > -500 ? { type: 'drop', msUntil: ms } : null; }
    if (ev && ev.type === 'drop' && ev.msUntil != null) {
      const lead = DROP_LEAD_BEATS * beatS * 1000;
      if (ev.msUntil > 0 && ev.msUntil <= lead) this._dropLead = 1 - ev.msUntil / lead;
      if (ev.msUntil <= 0 && this._time - this._lastDropTime > 2.0) { this._lastDropTime = this._time; this._dropT = 0; this._dropLead = 0; }
    } else {
      this._dropLead = 0;
    }

    // --- the flow rate, per second
    const rate = Math.log(SINK_REACH) / SINK_BEATS / beatS;            // halfway to the sink in 8 beats
    let flow = 0;
    if (this._mode !== 'elliptic' && !this._forcedK) {
      flow = rate * this._dir * (1 + LOW_SWING * (this._lowBand * 2 - 1)) * (1 + 2.0 * this._dropLead);
      // the bar's impulse: BAR_IMPULSE over BAR_IMPULSE_S
      if (this._impulseT < BAR_IMPULSE_S) {
        const t0 = this._impulseT, t1 = Math.min(BAR_IMPULSE_S, t0 + dt);
        flow += BAR_IMPULSE * this._dir * ((smooth(t1 / BAR_IMPULSE_S) - smooth(t0 / BAR_IMPULSE_S)) / dt);
      }
      this._impulseT += dt;
    }
    if (this._dropT >= 0) {
      const t0 = this._dropT, t1 = t0 + dt;
      const implode = beatS, rebirth = DROP_REBIRTH_BARS * BEATS_PER_BAR * beatS;
      if (t0 < implode) {
        // nearly the whole plane into the sink, over one beat
        flow += DROP_IMPLODE * this._dir * ((smooth(Math.min(1, t1 / implode)) - smooth(t0 / implode)) / dt);
      } else if (t0 < implode + rebirth) {
        // then out again, violently: the implosion and one more sink-reach undone over two bars
        const u0 = (t0 - implode) / rebirth, u1 = Math.min(1, (t1 - implode) / rebirth);
        flow = -(DROP_IMPLODE + Math.log(SINK_REACH)) * this._dir * ((smooth(u1) - smooth(u0)) / dt);
      } else {
        this._dropT = -1;
        this._dir = -this._dir;                        // the rebirth keeps flowing outward
      }
      if (this._dropT >= 0) this._dropT += dt;
    }
    this._a += flow * dt;
    // past A_MAX the frame is a grey of mipmaps: turn the flow around early
    if (Math.abs(this._a) > A_MAX && Math.sign(this._a) === this._dir && this._dropT < 0) this._dir = -this._dir;

    // --- the rotation step
    this._stepT += dt;
    this._theta = lerp(this._thetaFrom, this._thetaTo, smooth(this._stepT / STEP_S));
    if (this._stepDebug && !this._stepDone && this._time > 1.0) { this._stepDone = true; this._step(); }

    // --- the second map (DOUBLE): slower, turning the other way
    if (this._mode === 'double') {
      this._a2 += flow * DOUBLE_RATE * dt * 0.5;
      this._theta2 = -this._theta * DOUBLE_RATE;
    }

    if (this._forcedK) { this._a = this._forcedK[0]; this._theta = this._forcedK[1]; }
    this._uniforms.uK.value.set(this._a, this._theta);
    this._uniforms.uK2.value.set(this._a2, this._theta2);
    this._uniforms.uDouble.value = this._mode === 'double' ? 1 : 0;

    // --- the points, the scale, the bands
    this._placePoints(dt);
    this._scaleT += dt;
    this._scale = lerp(this._scaleFrom, this._scaleTo, smooth(this._scaleT / (JUMP_BEATS * beatS)));
    this._uniforms.uScale.value = this._scale * (1 + MID_SWING * (this._midBand * 2 - 1));
    this._uniforms.uAberration.value = this._layout.aberration ? (this._live ? 0.35 + 0.65 * this._highBand : 0.6) : 0;
  }

  _setPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : FALLBACK_VIVID;
    const c = hexToRgb(vivid[0]);
    const mx = Math.max(c[0], c[1], c[2], 1e-3);
    this._tint = [c[0] / mx, c[1] / mx, c[2] / mx];
    this._uniforms.uTint.value.set(this._tint[0], this._tint[1], this._tint[2]);
    this._uniforms.uColA.value.set(...hexToRgb(vivid[0]));
    this._uniforms.uColB.value.set(...hexToRgb(vivid[1 % vivid.length]));
  }

  _loadCover(url) {
    const self = this;
    const token = ++this._loadToken || (this._loadToken = 1);
    loadArtworkTexture(url, { wrap: THREE.MirroredRepeatWrapping, mipmaps: true }, function (tex) {
      if (!self._material || token !== self._loadToken) { tex.dispose(); return; }
      self._setTexture(tex);
    }, function () { self._setTexture(null); });
  }
  _setTexture(tex) {
    if (this._texture) this._texture.dispose();
    this._texture = tex;
    this._uniforms.uTex.value = tex;
    this._uniforms.uHasTex.value = tex ? 1 : 0;
    if (tex && tex.image) this._uniforms.uTexSize.value.set(tex.image.width || 1024, tex.image.height || 1024);
  }

  // --- for the tests ------------------------------------------------------------
  get state() { return { a: this._a, theta: this._theta, thetaTo: this._thetaTo, dir: this._dir, p: this._p.slice(), q: this._q.slice(), mode: this._mode, scale: this._scale, drop: this._dropT, anchor: this._anchor }; }
  get uniforms() { return this._uniforms; }
  get textureSize() { return this._texture && this._texture.image ? [this._texture.image.width, this._texture.image.height] : null; }

  dispose() {
    if (this._mesh) {
      this._mesh.onBeforeRender = null;
      if (this._scene) this._scene.remove(this._mesh);
      this._geo.dispose();
      this._material.dispose();
    }
    if (this._texture) this._texture.dispose();
    this._texture = null;
    this._mesh = null;
    this._material = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
